<template>
  <small-modal
    class="thumbnail-modal"
    :show-close-button="false"
    @close="closeModal"
  >
    <div class="video-editor-modal">
      <div class="video-editor-header">
        <h2 class="title">{{ $t("create.step2.editThumbnailModal.title") }}</h2>
        <div class="header-buttons">
          <button class="header-button back" @click="closeModal">
            {{ $t("create.step2.addChaptersModal.back") }}
            <SquaredCross class="squared-cross" />
          </button>
          <button
            class="header-button save"
            @click="isChunkSelecting ? saveChunk() : saveThumbnail()"
          >
            {{ $t("create.step2.addChaptersModal.save") }}
            <SquaredCheck />
          </button>
        </div>
      </div>

      <div class="video-container">
        <div class="video-wrapper">
          <video
            ref="videoPlayer"
            :src="videoBlobUrl || video.url"
            class="video-player"
            @timeupdate="updateCurrentTime"
            @play="handlePlay"
            @pause="handlePause"
            @ended="handleEnded"
          ></video>
          <button class="mute-button" @click="toggleMute">
            <Unmute v-if="isMuted" />
            <Mute v-else />
          </button>
        </div>
      </div>

      <div class="actions">
        <button
          v-tippy="{ placement: 'top', arrow: true }"
          class="play-pause-button"
          :content="'Space bar'"
          @click="togglePlay"
        >
          <PauseIcon v-if="isPlaying" class="play-pause-icon" />
          <PlayIcon v-else class="play-pause-icon" />
        </button>
        <div v-if="!isSelecting && !isChunkSelecting" class="pack-buttons">
          <div class="edit-thumbnail-button">
            <button
              class="play-pause-button"
              :class="{ 'selected-button': showThumbnailOptions }"
              @click="showThumbnailOptions = true"
            >
              <UpdateThumbnailIcon />
              {{ $t("create.step2.editThumbnailModal.title") }}
            </button>
            <div
              v-if="showThumbnailOptions"
              v-click-outside="closeThumbnailOptions"
              class="thumbnail-options-modal"
            >
              <p class="options-title">
                {{ $t("create.step2.editThumbnailModal.options") }}
              </p>
              <button class="option-button" @click="enterSelectionMode">
                {{ $t("create.step2.editThumbnailModal.select") }}
              </button>
              <button class="option-button" @click="$refs.fileInput.click()">
                {{ $t("create.step2.editThumbnailModal.upload") }}
              </button>
              <button class="option-button" @click="enterChunkMode">
                {{ $t("create.step2.editThumbnailModal.loop") }}
              </button>
            </div>
          </div>
        </div>

        <div v-else class="pack-buttons">
          <span v-if="!isChunkSelecting" class="selection-info">
            {{ $t("create.step2.editThumbnailModal.frameAndSave") }}
          </span>
          <span v-else class="selection-info">
            {{ $t("create.step2.editThumbnailModal.chunkAndSave") }}
          </span>
          <SquaredCrossIcon
            class="squared-cross-icon"
            @click="leaveSelectorMode"
          />
        </div>

        <div class="time">
          <span :class="{ 'duration-active': currentTime > 0 }">
            {{ formattedTime.current }}
          </span>
          <span class="separator">/</span>
          <span>{{ formattedTime.duration }}</span>
        </div>
      </div>

      <div class="timeline-container">
        <div
          ref="timelineWrapper"
          class="timeline-wrapper"
          @mousedown="startDragging"
          @mousemove="handleDragging"
          @mouseup="stopDragging"
          @mouseleave="stopDragging"
        >
          <div class="time-ruler">
            <div
              v-for="(marker, index) in timeMarkers"
              :key="index"
              class="time-marker"
              :style="{ left: marker.position + 'px' }"
            >
              {{ marker.time }}
            </div>
          </div>
          <div ref="timeline" class="timeline">
            <img
              v-for="(frame, index) in frames"
              :key="index"
              class="frame"
              :src="frame"
              :style="{ width: frameWidth + 'px' }"
            />
            <div
              v-if="isSelecting"
              class="selection-line"
              :style="{ transform: `translateX(${selectionLinePosition}px)` }"
              @mousedown.prevent="startLineDragging"
            ></div>
            <div
              v-if="isChunkSelecting"
              class="selection-chunk"
              :style="{
                transform: `translateX(${chunkPosition}px)`,
                width: chunkWidth + 'px',
              }"
              @mousedown.prevent="startChunkDragging"
              @mousemove="handleChunkDrag"
              @mouseup="stopChunkDragging"
              @mouseleave="stopChunkDragging"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <input
      ref="fileInput"
      style="display: none"
      accept=".jpeg, .jpg, .png"
      type="file"
      @change="onFileChanged"
    />
  </small-modal>
</template>

<script>
import SmallModal from "@/components/shared/SmallModal.vue";
import SquaredCross from "@/assets/svg/squared-cross-icon.svg?inline";
import SquaredCheck from "@/assets/svg/squared-check-big.svg?inline";
import SplitIcon from "@/assets/svg/split-icon.svg?inline";
import PlayIcon from "@/assets/svg/play-icon-no-bg.svg?inline";
import PauseIcon from "@/assets/svg/pause-icon.svg?inline";
import Mute from "@/assets/svg/mute-icon.svg?inline";
import Unmute from "@/assets/svg/sound-off.svg?inline";
import SquaredCrossIcon from "@/assets/svg/squared-cross-icon.svg?inline";
import UpdateThumbnailIcon from "@/assets/svg/update-thumbnail-icon.svg?inline";

export default {
  components: {
    SmallModal,
    SquaredCross,
    SquaredCheck,
    SplitIcon,
    PlayIcon,
    PauseIcon,
    Mute,
    Unmute,
    SquaredCrossIcon,
    UpdateThumbnailIcon,
  },
  props: {
    video: {
      type: Object,
      required: true,
    },
    currentThumbnail: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      frames: [],
      videoDuration: 0,
      currentTime: 0,
      hasStartedPlaying: false,
      frameInterval: 1,
      selectionLinePosition: 0,
      frameWidth: 48,
      isDragging: false,
      isLineDragging: false,
      timelineOffset: 0,
      wasPlaying: false,
      isPlaying: false,
      timelineScrollWidth: 0,
      isMuted: false,
      videoBlob: null,
      videoBlobUrl: null,
      renderedTime: 0,
      isSelecting: false,
      thumbnailBlob: null,
      selectedThumbnail: null,
      isChunkSelecting: false,
      chunkDuration: 4,
      chunkPosition: 0,
      isChunkDragging: false,
      showThumbnailOptions: false,
      hasSelectionChanged: false,
      framesGenerated: false,
    };
  },
  computed: {
    formattedTime() {
      return {
        current: this.formatTime(this.currentTime),
        duration: this.formatTime(this.videoDuration),
      };
    },
    timeMarkers() {
      if (!this.videoDuration || !this.$refs.timeline) return [];
      const timelineWidth = this.$refs.timeline.scrollWidth;
      const totalMarkers = 10;
      const interval = this.videoDuration / (totalMarkers - 1);
      const markers = [];
      const effectiveWidth = timelineWidth - 26;
      const startOffset = 13;
      for (let i = 0; i < totalMarkers; i++) {
        let position =
          ((i * interval) / this.videoDuration) * effectiveWidth + startOffset;
        if (i === totalMarkers - 1) {
          position = timelineWidth - startOffset;
        }
        markers.push({
          time: this.formatMarkerTime(i * interval),
          position,
        });
      }
      return markers;
    },
    chunkWidth() {
      return this.videoDuration > 0 && this.timelineScrollWidth > 0
        ? (this.chunkDuration / this.videoDuration) * this.timelineScrollWidth
        : 0;
    },
  },
  watch: {
    "video._id": {
      immediate: true,
      handler() {
        this.frames = [];
        this.videoBlob = null;
        this.videoBlobUrl = null;
        this.framesGenerated = false;
        this.fetchBlobAndGenerate();
      },
    },
    currentTime(newTime) {
      if (!this.isDragging && !this.isLineDragging) {
        this.updateSelectionLine();
      }
    },
  },
  async mounted() {
    if (this.video?.trimmedClipStart) {
      this.currentTime = this.video.trimmedClipStart;
    }

    this.$nextTick(() => {
      if (this.$refs.timeline) {
        this.timelineScrollWidth = this.$refs.timeline.scrollWidth;
      }
    });
    this.startAnimationLoop();
  },
  beforeUnmount() {
    if (this.videoBlobUrl) {
      URL.revokeObjectURL(this.videoBlobUrl);
    }
    cancelAnimationFrame(this.animationId);
  },
  methods: {
    enterSelectionMode() {
      this.isSelecting = true;
      this.$refs.videoPlayer.pause();
      this.hasSelectionChanged = false;
    },

    saveThumbnail() {
      if (!this.hasSelectionChanged) {
        return this.closeModal();
      }
      const videoEl = this.$refs.videoPlayer;
      const canvas = document.createElement("canvas");
      canvas.width = videoEl.videoWidth;
      canvas.height = videoEl.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (!blob) {
          return this.$notify({
            title: this.$t("shared.toastMessage.error"),
            text: "Screenshot failed, try to upload an image",
            type: "error",
          });
        }
        this.$emit("thumbnail-selected", {
          blob: URL.createObjectURL(blob),
          tempThumbnailFile: blob,
        });
        this.closeModal();
      }, "image/png");
    },

    onFileChanged(event) {
      const file = event.target.files[0];
      this.selectedThumbnail = URL.createObjectURL(file);
      this.thumbnailBlob = file;
      this.hasSelectionChanged = true;
      this.$emit("thumbnail-selected", {
        blob: this.selectedThumbnail,
        tempThumbnailFile: file,
      });
      this.closeModal();
    },

    async generateFrames() {
      if (this.framesGenerated) return;
      this.framesGenerated = true;
      try {
        if (!this.videoBlob) return;
        const tempVideo = document.createElement("video");
        tempVideo.src = this.videoBlobUrl;
        await new Promise((resolve) =>
          tempVideo.addEventListener("loadedmetadata", resolve)
        );
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const aspectRatio = tempVideo.videoWidth / tempVideo.videoHeight;
        canvas.height = 80;
        canvas.width = canvas.height * aspectRatio;
        this.frames = [];
        const totalFrames = 20;
        const frameInterval = tempVideo.duration / (totalFrames - 1);
        this.videoDuration = tempVideo.duration;
        for (let i = 0; i < totalFrames; i++) {
          await new Promise((resolve) => {
            tempVideo.currentTime = i * frameInterval;
            tempVideo.addEventListener(
              "seeked",
              () => {
                ctx.drawImage(tempVideo, 0, 0, canvas.width, canvas.height);
                this.frames.push(canvas.toDataURL("image/jpeg", 0.7));
                resolve();
              },
              { once: true }
            );
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    updateCurrentTime() {
      if (
        !this.$refs.videoPlayer ||
        this.isDragging ||
        this.isLineDragging ||
        !this.hasStartedPlaying
      )
        return;
      this.currentTime = this.$refs.videoPlayer.currentTime;
    },
    updateSelectionLine() {
      const timeline = this.$refs.timeline;
      if (!timeline || !this.videoDuration) return;
      this.selectionLinePosition =
        (this.currentTime / this.videoDuration) * timeline.scrollWidth;
    },
    startLineDragging(e) {
      this.wasPlaying = !this.$refs.videoPlayer.paused;
      this.$refs.videoPlayer.pause();
      this.isLineDragging = true;
      this.timelineOffset =
        this.$refs.timelineWrapper.getBoundingClientRect().left;
      this.handleDrag(e);
      this.hasSelectionChanged = true;
    },
    startDragging(e) {
      if (this.isSelecting) {
        this.hasSelectionChanged = true;
      }
      this.wasPlaying = !this.$refs.videoPlayer.paused;
      this.$refs.videoPlayer.pause();
      this.isDragging = true;
      this.timelineOffset =
        this.$refs.timelineWrapper.getBoundingClientRect().left;
      this.handleDrag(e);
    },
    handleDragging(e) {
      if (this.isDragging || this.isLineDragging) {
        this.handleDrag(e);
      }
    },
    handleDrag(e) {
      const wrapper = this.$refs.timelineWrapper;
      const timeline = this.$refs.timeline;
      const rect = wrapper.getBoundingClientRect();
      const scrollLeft = wrapper.scrollLeft;
      let posX = e.clientX - rect.left + scrollLeft;
      posX = Math.max(0, Math.min(posX, timeline.scrollWidth));
      const time = (posX / timeline.scrollWidth) * this.videoDuration;
      this.$refs.videoPlayer.currentTime = time;
      this.currentTime = time;
      this.selectionLinePosition = posX;
    },
    stopDragging() {
      this.isDragging = false;
      this.isLineDragging = false;
    },
    closeModal() {
      this.$emit("close");
    },
    togglePlay() {
      if (this.$refs.videoPlayer.paused) {
        this.$refs.videoPlayer.play();
      } else {
        this.$refs.videoPlayer.pause();
      }
    },
    handlePlay() {
      this.isPlaying = true;
      this.hasStartedPlaying = true;
      const update = () => {
        if (!this.$refs.videoPlayer.paused) {
          this.currentTime = this.$refs.videoPlayer.currentTime;
          requestAnimationFrame(update);
        }
      };
      requestAnimationFrame(update);
    },
    handlePause() {
      this.isPlaying = false;
    },
    handleEnded() {
      this.isPlaying = false;
    },
    toggleMute() {
      this.$refs.videoPlayer.muted = !this.isMuted;
      this.isMuted = !this.isMuted;
    },
    startAnimationLoop() {
      const animate = () => {
        if (!this.$refs.videoPlayer || this.isDragging || this.isLineDragging) {
          this.animationId = requestAnimationFrame(animate);
          return;
        }
        const delta = this.currentTime - this.renderedTime;
        this.renderedTime += delta * 0.45;
        this.selectionLinePosition = Math.max(
          0,
          Math.min(
            (this.renderedTime / this.videoDuration) * this.timelineScrollWidth,
            this.timelineScrollWidth
          )
        );
        this.animationId = requestAnimationFrame(animate);
      };
      this.animationId = requestAnimationFrame(animate);
    },
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = (seconds % 60).toFixed(2);
      return `${String(mins).padStart(2, "0")}:${secs.padStart(5, "0")}`;
    },
    formatMarkerTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${String(minutes).padStart(2, "0")}:${String(
        remainingSeconds
      ).padStart(2, "0")}`;
    },
    enterChunkMode() {
      this.isChunkSelecting = true;
      this.$refs.videoPlayer.pause();
      this.chunkPosition = this.selectionLinePosition;
    },

    startChunkDragging(e) {
      e.preventDefault();
      this.isChunkDragging = true;
      this.timelineOffset =
        this.$refs.timelineWrapper.getBoundingClientRect().left;
      const scrollLeft = this.$refs.timelineWrapper.scrollLeft;
      this.chunkDragOffset =
        e.clientX - this.timelineOffset + scrollLeft - this.chunkPosition;
      document.addEventListener("mousemove", this.handleChunkDrag);
      document.addEventListener("mouseup", this.stopChunkDragging);
    },

    handleChunkDrag(e) {
      if (!this.isChunkDragging) return;
      const scrollLeft = this.$refs.timelineWrapper.scrollLeft;
      let posX = e.clientX - this.timelineOffset + scrollLeft;
      posX -= this.chunkDragOffset;
      posX = Math.max(
        0,
        Math.min(posX, this.timelineScrollWidth - this.chunkWidth)
      );
      this.chunkPosition = posX;
      const t = (posX / this.timelineScrollWidth) * this.videoDuration;
      this.$refs.videoPlayer.currentTime = t;
      this.currentTime = t;
    },

    stopChunkDragging() {
      this.isChunkDragging = false;
      document.removeEventListener("mousemove", this.handleChunkDrag);
      document.removeEventListener("mouseup", this.stopChunkDragging);
    },

    saveChunk() {
      const start = this.currentTime;
      const end = start + this.chunkDuration;

      //If we already have the same trimmed url do not save again
      if (start === this.video?.trimmedClipStart) return this.closeModal();

      this.$emit("trim-chunk-selected", {
        start: this.currentTime,
        duration: this.chunkDuration,
      });

      this.closeModal();
    },
    leaveSelectorMode() {
      this.isSelecting = false;
      this.isChunkSelecting = false;
    },
    closeThumbnailOptions() {
      this.showThumbnailOptions = false;
    },
    async fetchBlobAndGenerate() {
      try {
        const response = await fetch(this.video.url);
        this.videoBlob = await response.blob();
        this.videoBlobUrl = URL.createObjectURL(this.videoBlob);
        await this.generateFrames();
      } catch (err) {
        console.error(err);
      }
    },
  },
};
</script>

<style lang="scss">
.thumbnail-modal .small-modal.bigger {
  width: 1000px;
  max-width: 1000px;
  overflow: hidden;
}
</style>

<style lang="scss" scoped>
.modal-background {
  background-color: rgba(0, 0, 0, 0.7) !important;
}

.header-buttons {
  display: flex;
  gap: 16px;
  align-items: center;
}

.header-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(123, 1, 181, 1);
  color: white;
  border: 1px solid rgba(123, 1, 181, 1);
  border-radius: 2px;

  &:hover {
    border: 1px solid rgba(123, 1, 181, 1);
    background: white;
    color: rgba(123, 1, 181, 1);
    svg {
      path {
        fill: rgba(123, 1, 181, 1);
      }
    }
  }
}

.back {
  background: white;
  border: 1px solid rgba(196, 196, 196, 1);
  color: rgba(196, 196, 196, 1);
  box-sizing: border-box;
}

.squared-cross {
  path {
    fill: rgba(196, 196, 196, 1);
  }
}
.timeline-container {
  height: 130px;
  margin-bottom: 24px;
  padding: 0 12px;
  position: relative;
}
.timeline-wrapper {
  position: relative;
  height: 100%;
}
.time-ruler {
  position: absolute;
  top: 0px;
  left: 0;
  right: 0;
  height: 12px;
  color: $dark-grey;
  font-size: 12px;
  pointer-events: none;
  border-top: 1px solid $light-grey;
  overflow: visible;
  margin-bottom: 12px;
  z-index: 2;
}

.time-marker {
  position: absolute;
  bottom: 0;
  transform: translateX(-50%);
  padding: 2px;
  white-space: nowrap;
  @include base-font;
  font-size: 8px;
  color: $light-grey;
  border-left: 1px solid $light-grey;

  &::after {
    content: "";
    position: absolute;
    bottom: -3px;
    left: 50%;
    width: 1px;
    height: 5px;
    background: white;
  }
}

.timeline {
  display: flex;
  height: 80px;
  background: #333;
  position: relative;
  align-items: center;
  top: 30px;
  z-index: 1;

  .frame {
    height: 80px;
    width: 48px;
    object-fit: cover;
    object-position: center;
    flex-shrink: 0;
    border-right: 1px solid #444;
    display: block;
  }
}

.selection-line {
  position: absolute;
  top: -13px;
  bottom: -2px;
  width: 3px;
  background: rgba(123, 1, 181, 1);
  cursor: col-resize;
  border-top: 3px solid rgba(123, 1, 181, 1);
  z-index: 20;
  transition: none;
  transform: translate3d(0, 0, 0);
  will-change: transform;
  backface-visibility: hidden;
  transform-style: preserve-3d;

  &::before {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-top: 12px solid rgba(123, 1, 181, 1);
  }

  &::after {
    content: "";
    position: absolute;
    top: -19px;
    left: 50%;
    transform: translateX(-50%);
    width: 12px;
    height: 8px;
    background: rgba(123, 1, 181, 1);
    border-radius: 0;
  }

  &:hover {
    width: 5px;
    border-top: 5px solid rgba(123, 1, 181, 1);
    top: -16px;

    &::before {
      border-left-width: 8px;
      border-right-width: 8px;
    }

    &::after {
      width: 15.5px;
    }
  }
}

.time-display {
  text-align: center;
  padding: 10px;
  font-weight: bold;
  color: #fff;
}

.actions {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 12px;
  padding: 0 16px;
  position: relative;
}

.pack-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}
.play-pause-button {
  @include base-font;
  padding: 6px 12px;
  background: rgba(123, 1, 181, 1);
  color: white;
  border: none;
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  border: 1px solid rgba(123, 1, 181, 1);
}

.play-pause-button:hover {
  background: white;
  border: 1px solid rgba(123, 1, 181, 1);
  color: rgba(123, 1, 181, 1);
  svg {
    path {
      fill: rgba(123, 1, 181, 1);
    }
  }
}

.video-container {
  display: flex;
  width: 100%;
  justify-content: center;
  height: 350px;
  background-color: rgba(217, 217, 217, 1);
  margin-bottom: 12px;
  position: relative;
  overflow: hidden;
}
.video-wrapper {
  position: relative;
  max-width: 100%;
  max-height: 100%;
}

.video-player {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}

.mute-button {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 10;
  background: rgba(60, 60, 60, 0.6);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  width: 25px;
  height: 25px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    width: 30px;
    height: 30px;
    background: rgba(60, 60, 60, 0.6);
    border: none;
    path {
      fill: white;
    }
  }
}

.video-editor-header {
  padding: 16px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title {
  @include base-font;
  @include font-big;
  font-weight: 600;
}
.time {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  @include base-font;
  @include font-smaller;
  display: flex;
  gap: 4px;
  align-items: center;

  .duration-active {
    color: $dark-purple;
    display: inline-block;
    min-width: 60px;
    font-feature-settings: "tnum";
    font-variant-numeric: tabular-nums;
  }
}

.selection-chunk {
  position: absolute;
  top: -2px;
  bottom: -2px;
  background: rgba(123, 1, 181, 0.3);
  border: 2px solid rgba(123, 1, 181, 1);
  cursor: move;
  z-index: 15;
}

.option-button {
  background: none;
  @include base-font;
  @include font-small;
  padding: 4px 8px;
  border-radius: 2px;
  outline: none;
  text-align: start;
  &:hover {
    color: white;
    background-color: $dark-purple;
  }
}

.options-title {
  @include base-font;
  font-size: 12px;
  font-weight: 700;
  line-height: 17px;
  color: rgba(163, 163, 163, 1);
  margin-left: 8px;
}

.edit-thumbnail-button {
  position: relative;

  .thumbnail-options-modal {
    box-shadow: 0px 0px 6px 1px rgba(0, 0, 0, 0.25);
    padding: 16px;
    width: 130px;
    min-height: 140px;
    z-index: 99;
    background: white;
    position: absolute;
    bottom: 40px;
    left: -15px;
    border-radius: 4px;
    border: 1px solid rgba(196, 196, 196, 1);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 16px;
  }
}
.selection-info {
  @include base-font;
  @include font-smaller;
  color: $dark-purple;
}

.squared-cross-icon {
  transform: scale(90%);
  cursor: pointer;
}

.selected-button {
  box-shadow: inset 0 0 0 1000px rgba(0, 0, 0, 0.2);
}
</style>
